import { getAuthHeaders } from '@/lib/api-utils'

/**
 * Unified Issue Update Service
 *
 * Single entry point for ALL issue updates across the application.
 * Handles both:
 *   1. Standard Redmine fields (status, priority, assignee, notes, etc.)
 *      → routed via /api/issues/:id (Redmine REST API proxy)
 *   2. Agile plugin data (sprint assignment, story points)
 *      → routed via /api/db/issues/:id (direct DB proxy)
 *
 * WHY: Agile data (sprints, story_points) lives in plugin tables that are
 * NOT exposed by the Redmine REST API, so we access them through our own
 * backend database proxy.
 */

export interface IssueUpdateData {
    /** Standard Redmine issue fields (passed to Redmine REST API) */
    issue?: {
        status_id?: number
        priority_id?: number
        assigned_to_id?: number | string
        tracker_id?: number
        subject?: string
        description?: string
        done_ratio?: number
        estimated_hours?: number | null
        start_date?: string | null
        due_date?: string | null
        fixed_version_id?: number | null
        parent_issue_id?: number | null
        category_id?: number | null
        notes?: string
        private_notes?: boolean
        [key: string]: any
    }
    /** Agile plugin data (passed to our DB proxy) */
    agile?: {
        sprintId?: number | null
        storyPoints?: number | null
    }
}

export interface IssueUpdateResult {
    success: boolean
    error?: string
    /** Result from Redmine API update (if issue fields were provided) */
    redmineResult?: any
    /** Result from agile data update (if agile fields were provided) */
    agileResult?: any
}

/**
 * Update an issue — the ONE function to call from anywhere in the app.
 *
 * @param issueId - Redmine issue ID
 * @param data    - Object containing `issue` (Redmine fields) and/or `agile` (sprint/story points)
 * @returns Unified result with success flag and optional error message
 *
 * @example
 * // Update status only (Redmine API)
 * await updateIssue(123, { issue: { status_id: 5 } })
 *
 * @example
 * // Assign to sprint only (DB proxy)
 * await updateIssue(123, { agile: { sprintId: 7, storyPoints: 5 } })
 *
 * @example
 * // Update both at once
 * await updateIssue(123, {
 *   issue: { status_id: 5, assigned_to_id: 42 },
 *   agile: { sprintId: 7, storyPoints: 5 }
 * })
 */
export async function updateIssue(
    issueId: number,
    data: IssueUpdateData,
): Promise<IssueUpdateResult> {
    const authHeaders = getAuthHeaders()
    const result: IssueUpdateResult = { success: true }

    // ── 1. Update Redmine fields via Redmine REST API ──────────────────
    if (data.issue && Object.keys(data.issue).length > 0) {
        try {
            const response = await fetch(`/api/issues/${issueId}`, {
                method: 'PUT',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ issue: data.issue }),
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                let errorMsg = errorData.error || 'Failed to update issue'

                if (response.status === 403) {
                    errorMsg = errorData.error || 'This status transition is not allowed by the workflow.'
                } else if (response.status === 422 && errorData.errors) {
                    const errors = Array.isArray(errorData.errors) ? errorData.errors : [errorData.errors]
                    errorMsg = errors.join(', ')
                }

                return {
                    success: false,
                    error: errorMsg,
                }
            }

            result.redmineResult = await response.json().catch(() => ({}))
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Network error updating issue',
            }
        }
    }

    // ── 2. Update agile data via DB proxy ──────────────────────────────
    if (data.agile && (data.agile.sprintId !== undefined || data.agile.storyPoints !== undefined)) {
        console.log('[issue-service] Updating agile data for issue', issueId, 'with:', data.agile)
        try {
            const response = await fetch(`/api/db/issues/${issueId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data.agile),
            })

            console.log('[issue-service] Agile update response status:', response.status)

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                console.error('[issue-service] Agile update failed:', errorData)
                return {
                    success: false,
                    error: errorData.error || 'Failed to update agile data',
                    redmineResult: result.redmineResult, // preserve partial success
                }
            }

            result.agileResult = await response.json().catch(() => ({}))
            console.log('[issue-service] Agile update result:', result.agileResult)
        } catch (error) {
            console.error('[issue-service] Agile update error:', error)
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Network error updating agile data',
                redmineResult: result.redmineResult,
            }
        }
    }

    return result
}
